// ABOUTME: All external channels view for warehouse settings - displays data grid
// ABOUTME: Shows all configured external channels with ability to add new ones and view details

import { Box, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { tokens } from "../../../../../../theme";
import Text from "../../../../../../components/theme/text/Text";
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ButtonGroup from "../../../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../../../components/theme/buttons/Shbutton";
import ShDataGrid2 from "../../../../../../components/theme/datagrid2/ShDataGrid2";
import { useGetExternalChannelsQuery } from "../../../../../../newapi/externalchannel/externalChannelSlice";
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import MpAddExternalChannel from "../../../../../../components/global/ModalsProgression/MpAddExternalChannel/MpAddExternalChannel";
import ShTextSkeleton from "../../../../../../components/theme/skeleton/ShTextSkeleton";
import SidepanelLoader from "../../../../../../components/theme/sidepanel2/SidepanelLoader";
import { selectUserPermission } from "../../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../../utils/permissions";
import ShLabel from "../../../../../../components/theme/label/ShLabel";
import ShLabelSkeleton from "../../../../../../components/theme/skeleton/ShLabelSkeleton";
import { WarehouseExternalChannelCard } from "../../../../../../components/global/cards/other/ExternalChannelCard";
import { ReactComponent as PredikoLogo } from '../../../../../../styles/svg/prediko.svg';
import { ReactComponent as ReturnlessLogo } from '../../../../../../styles/svg/returnless.svg';
import PublicIcon from '@mui/icons-material/Public';
import { ReactComponent as AmpereLogo } from '../../../../../../styles/svg/ampere.svg';

const SpExternalChannelDetails = SidepanelLoader(() => import("../../../../../../components/global/Sidepanels/SpExternalChannelDetails/SpExternalChannelDetails"));

const GridActions = (props, i18n) => {
    const theme = useTheme();
    useEffect(() => { }, [props.open]);
    const { t } = useTranslation();

    return (
        <ButtonGroup version={2}>
            {props.hasCreatePermission && (
                <Shbutton
                    onClick={() => props.handleOpen(true)}
                    className={"group-btn"}
                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                    variant="contained"
                    endIcon={<AddIcon />}>{t("add_external_channel")}</Shbutton>
            )}
        </ButtonGroup>
    );
}

let gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const All = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isExtended, setIsExtended] = useState(false);
    const [config, setConfig] = useState({all_credentials: true});
    const [spLoading, setSpLoading] = useState();

    const hasCreatePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.EXTERNALCHANNELS_MANAGE])
    );

    const [addModalOpen, setAddModalOpen] = useState(false);
    const addModal = {
        open: addModalOpen,
        setOpen: setAddModalOpen
    }

    const handleClose = () => {
        setAddModalOpen(false);
    };

    const [channelId, setChannelId] = useState();
    const channelIdRef = useRef(channelId);

    const openChannel = (id) => {
        const newChannelId = parseInt(id);
        if (!newChannelId) return;

        if (channelIdRef.current !== newChannelId) {
            if (!spLoading) {
                setSpLoading(true);
            }
            setChannelId(newChannelId);
            channelIdRef.current = newChannelId;
        }
        setIsExtended(true);
    };

    const getChannelDetails = (params) => {
        if (!params) return;
        openChannel(params.row.id);
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            mobileActions: <GridActions handleOpen={() => setAddModalOpen(true)} hasCreatePermission={hasCreatePermission}></GridActions>
        });

        return () => {
            // cleanup
        }
    }, []);

    const friendlyChannelType = (type) => {
        switch (type) {
            case "prediko":
                return "Prediko";
            case "returnless":
                return "Returnless";
            case "zonos":
                return "Zonos";
            case "ampere":
                return "Ampère";
            default:
                return t('unknown');
        }
    };

    const getChannelLogo = (type) => {
        switch (type) {
            case "prediko":
                return <PredikoLogo style={{ width: 24, height: 24 }} />;
            case "returnless":
                return <ReturnlessLogo style={{ width: 24, height: 24 }} />;
            case "zonos":
                return <PublicIcon sx={{ width: 24, height: 24 }} />;
            case "ampere":
                return <AmpereLogo style={{ width: 24, height: 24 }} />;
            default:
                return <IntegrationInstructionsIcon sx={{ fontSize: 24 }} />;
        }
    };

    const columns = [
        {
            field: "description", headerName: t("channel_name"), flex: 2, cellClassName: "name", renderCell: (cellValues) => {
                if (cellValues.row.skeleton) {
                    return <ShTextSkeleton />
                }

                return (
                    <>
                        <Box sx={{ paddingRight: '10px' }}>
                            {getChannelLogo(cellValues.row.channel_type)}
                        </Box>
                        <Text semibold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{cellValues.value}</Text>
                    </>
                )
            }
        },
        {
            field: "channel_type", headerName: t("channel_type"), flex: 2, cellClassName: "type", renderCell: (cellValues) => {
                if (cellValues.row.skeleton) {
                    return <ShTextSkeleton />
                }

                return (
                    <Text>{friendlyChannelType(cellValues.value)}</Text>
                )
            }
        },
        {
            field: "state",
            headerName: t("status"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) {
                    return <ShLabelSkeleton />
                }

                const labelColor = params.row.state === "active" ? "green" : theme.palette.mode === "dark" ? "greyLight" : "grey";

                return (
                    <ShLabel size={32} palette="normal" variant={labelColor}>
                        {t(params.row.state)}
                    </ShLabel>
                );
            }
        },
        {
            field: "createdAt", headerName: t("created_at"), flex: 1, renderCell: (cellValues) => {
                if (cellValues.row.skeleton) {
                    return <ShTextSkeleton />
                }

                let date = new Date(cellValues.row.createdAt).toLocaleDateString(i18n.language);

                return (
                    <Text>{date}</Text>
                )
            }
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) {
                    return <ShTextSkeleton />
                }

                return (
                    <WarehouseExternalChannelCard
                        skeleton={cellValues.row.skeleton ?? false}
                        {...cellValues.row}
                        onClick={() => getChannelDetails(cellValues)}
                    />
                )
            }
        }
    ];

    return (
        <Box
            sx={{
                height: 1,
                "@media screen and (max-width: 47.9375em)": {
                    paddingTop: 0,
                    paddingX: 0,
                }
            }}
        >

            <ShDataGrid2 key={theme.palette.mode}
                content={useGetExternalChannelsQuery}
                config={config}
                gridActions={<GridActions handleOpen={() => setAddModalOpen(true)} hasCreatePermission={hasCreatePermission}></GridActions>}
                title={t("external_channels")}
                gridOptions={gridOptions}
                sortModel={{ field: columns[0].field, type: "ASC" }}
                columns={columns}
                onRowClick={getChannelDetails}>
            </ShDataGrid2>

            <MpAddExternalChannel onOpen={() => addModal.setOpen(true)} onClose={() => addModal.setOpen(false)} open={addModal.open} handleClose={handleClose} onOpenCreatedChannel={openChannel} />

            {spLoading &&
                <SpExternalChannelDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    channelId={channelId}
                />
            }
        </Box>
    )
};

export default All;
